import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useLanguage } from '../../../src/functions/useLanguage';
import { wt } from '../../i18n';
import { websitePath } from '../../lib/routes';

const META = {
  title: 'The Lab Story: Reading Your Pet’s Results with Confidence | KodaCare Blog',
  description:
    'Understand bloodwork and urinalysis by focusing on trends, not single numbers. Pair lab results with daily symptoms in KodaCare for clearer vet decisions.',
  slug: 'the-lab-story',
  dateISO: '2026-04-10',
  readMins: 7,
  tag: 'Labs',
};

type Lang = 'English' | 'French' | 'German' | 'Portuguese' | 'Spanish';

const LOCALE_BY_LANGUAGE: Record<Lang, string> = {
  English: 'en-GB',
  French: 'fr-FR',
  German: 'de-DE',
  Portuguese: 'pt-PT',
  Spanish: 'es-ES',
};

export function TheLabStory() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = (key: Parameters<typeof wt>[1]) => wt(language, key);
  const activeLanguage = language as Lang;

  const titleByLanguage: Record<string, string> = {
    English: 'The Lab Story: Reading Your Pet’s Results with Confidence',
    French: 'L’histoire des analyses : comprendre les résultats de votre animal',
    German: 'Die Labor-Story: Ergebnisse Ihres Haustiers sicher lesen',
    Portuguese: 'A história dos exames: interpretar resultados do seu animal',
    Spanish: 'La historia de los análisis: entender los resultados de tu mascota',
  };

  const descriptionByLanguage: Record<Lang, string> = {
    English:
      'Understand bloodwork and urinalysis by focusing on trends, not single numbers. Pair lab results with daily symptoms in KodaCare for clearer vet decisions.',
    French:
      'Comprenez les analyses sanguines et urinaires en regardant les tendances. Associez résultats et symptômes quotidiens dans KodaCare.',
    German:
      'Verstehen Sie Blut- und Urinwerte über Trends statt Einzelzahlen. Kombinieren Sie Laborwerte mit Alltagssymptomen in KodaCare.',
    Portuguese:
      'Entenda exames de sangue e urina olhando tendências. Combine resultados e sintomas diários no KodaCare.',
    Spanish:
      'Entiende análisis de sangre y orina con tendencias, no datos aislados. Combina resultados y síntomas diarios en KodaCare.',
  };

  const localizedTitle = `${titleByLanguage[activeLanguage] ?? titleByLanguage.English} | KodaCare Blog`;
  const localizedDescription = descriptionByLanguage[activeLanguage] ?? descriptionByLanguage.English;

  const tagLabel =
    META.tag === 'Labs'
      ? t('blog.tag.labs')
      : META.tag;

  const isEnglishContent = activeLanguage === 'English';
  const translatedSummary: Record<Exclude<Lang, 'English'>, {
    intro: string;
    sections: { heading: string; paragraphs: string[] }[];
    closing: string;
    series: string;
  }> = {
    French: {
      intro:
        'Les résultats d’analyses peuvent inquiéter. L’essentiel est de lire les tendances plutôt qu’un chiffre isolé.',
      sections: [
        {
          heading: '1. Un seul résultat ne raconte pas tout',
          paragraphs: [
            'Hydratation, stress et moment du prélèvement influencent les valeurs.',
            'Une tendance régulière est beaucoup plus fiable qu’un pic isolé.'
          ],
        },
        {
          heading: '2. Relier résultats et symptômes',
          paragraphs: [
            'Associez analyses à l’appétit, à l’énergie et à la respiration quotidienne.',
            'KodaCare met ces éléments en contexte pour votre vétérinaire.'
          ],
        },
        {
          heading: '3. Préparer de meilleures questions',
          paragraphs: [
            'Arrivez au rendez-vous avec une chronologie claire et quelques questions ciblées.',
            'Cela rend les décisions plus simples et plus rapides.'
          ],
        },
      ],
      closing: 'Comprendre la tendance vous donne de la clarté et protège la santé de votre animal.',
      series: 'KodaCare Journal · Article 3 sur 4',
    },
    German: {
      intro:
        'Laborwerte können verunsichern. Entscheidend ist der Verlauf, nicht der Einzelwert.',
      sections: [
        {
          heading: '1. Einzelwerte schwanken',
          paragraphs: [
            'Hydration, Fütterungszeit und Stress beeinflussen Ergebnisse.',
            'Ein Trend über Wochen sagt mehr als ein einzelner Test.'
          ],
        },
        {
          heading: '2. Laborwerte + Alltagssignale',
          paragraphs: [
            'Appetit, Energie und Atmung liefern den Kontext zu den Laborwerten.',
            'KodaCare verbindet beides in einer Übersicht.'
          ],
        },
        {
          heading: '3. Bessere Fragen stellen',
          paragraphs: [
            'Mit einer klaren Timeline kommen Sie schneller zu Entscheidungen.',
            'So wird das Gespräch mit dem Tierarzt effizienter.'
          ],
        },
      ],
      closing: 'Wer Trends versteht, trifft ruhigere und klarere Entscheidungen für sein Tier.',
      series: 'KodaCare Journal · Artikel 3 von 4',
    },
    Portuguese: {
      intro:
        'Resultados de exames podem assustar. O mais importante é observar tendências.',
      sections: [
        {
          heading: '1. Um valor isolado não explica tudo',
          paragraphs: [
            'Hidratação e momento da colheita influenciam os números.',
            'A tendência ao longo do tempo é o dado mais confiável.'
          ],
        },
        {
          heading: '2. Contexto diário faz diferença',
          paragraphs: [
            'Combine exames com apetite, energia e respiração do dia a dia.',
            'O KodaCare reúne esses sinais num só lugar.'
          ],
        },
        {
          heading: '3. Chegue com perguntas claras',
          paragraphs: [
            'Uma linha do tempo facilita decisões com o veterinário.',
            'Mais clareza, menos ansiedade.'
          ],
        },
      ],
      closing: 'Entender tendências ajuda a cuidar melhor e com mais confiança.',
      series: 'KodaCare Journal · Artigo 3 de 4',
    },
    Spanish: {
      intro:
        'Los análisis pueden generar ansiedad. La clave es mirar tendencias, no un valor aislado.',
      sections: [
        {
          heading: '1. Un dato no es toda la historia',
          paragraphs: [
            'Hidratación y horario de extracción cambian los números.',
            'La tendencia en el tiempo es lo que realmente importa.'
          ],
        },
        {
          heading: '2. Resultados con contexto diario',
          paragraphs: [
            'Relaciona análisis con apetito, energía y respiración diaria.',
            'KodaCare une todo en una misma vista.'
          ],
        },
        {
          heading: '3. Preguntas más claras',
          paragraphs: [
            'Una cronología sencilla mejora la conversación con el veterinario.',
            'Decisiones más rápidas, menos estrés.'
          ],
        },
      ],
      closing: 'Leer tendencias te ayuda a cuidar mejor y con más calma.',
      series: 'KodaCare Journal · Artículo 3 de 4',
    },
  };

  useEffect(() => {
    const prevTitle = document.title;
    document.title = localizedTitle;

    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    const prevDesc = metaDesc.content;
    metaDesc.content = localizedDescription;

    const upsertMeta = (selector: string, attr: 'property' | 'name', value: string) => {
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      const created = !el;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, selector.match(/\[.+?="(.+?)"\]/)?.[1] ?? '');
        document.head.appendChild(el);
      }
      const previous = el.content;
      el.content = value;
      return { el, created, previous };
    };

    const updates = [
      upsertMeta('meta[property="og:title"]', 'property', localizedTitle),
      upsertMeta('meta[property="og:description"]', 'property', localizedDescription),
      upsertMeta('meta[property="og:type"]', 'property', 'article'),
      upsertMeta('meta[property="og:url"]', 'property', `https://kodacare.pet/blog/${META.slug}`),
      upsertMeta('meta[name="twitter:card"]', 'name', 'summary_large_image'),
      upsertMeta('meta[name="twitter:title"]', 'name', localizedTitle),
      upsertMeta('meta[name="twitter:description"]', 'name', localizedDescription),
      upsertMeta('meta[name="twitter:image"]', 'name', 'https://kodacare.pet/icons/og-image.png'),
      upsertMeta('meta[property="og:image"]', 'property', 'https://kodacare.pet/icons/og-image.png'),
    ];

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    const canonicalCreated = !canonical;
    const previousCanonical = canonical?.href ?? '';
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `https://kodacare.pet/blog/${META.slug}`;

    return () => {
      document.title = prevTitle;
      if (metaDesc) metaDesc.content = prevDesc;

      updates.forEach(({ el, created, previous }) => {
        if (created) {
          el.remove();
        } else {
          el.content = previous;
        }
      });

      if (canonical) {
        if (canonicalCreated) {
          canonical.remove();
        } else {
          canonical.href = previousCanonical;
        }
      }
    };
  }, [localizedDescription, localizedTitle]);

  return (
    <main className="min-h-screen bg-white">
      <div className="bg-[#0d2744] pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <button
            onClick={() => navigate(websitePath('/blog'))}
            className="flex items-center gap-2 text-primary-brown/80 hover:text-white text-sm mb-8 transition-colors"
          >
            <ArrowLeft size={16} /> {t('blog.backToBlog')}
          </button>
          <span className="inline-block text-xs font-bold px-3 py-1 rounded-full bg-primary-brown/15 text-primary-brown mb-4">
            {tagLabel}
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight mb-6">
            {titleByLanguage[language] ?? titleByLanguage.English}
          </h1>
          <div className="flex items-center gap-3 text-sm text-primary-brown/80">
            <span>{new Date(`${META.dateISO}T00:00:00`).toLocaleDateString(LOCALE_BY_LANGUAGE[activeLanguage] ?? 'en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
            <span>·</span>
            <span>{META.readMins} {t('blog.minRead')}</span>
            <span>·</span>
            <span>{t('blog.authorTeam')}</span>
          </div>
        </div>
      </div>

      {!isEnglishContent && (
        <article className="max-w-3xl mx-auto px-6 lg:px-10 py-16 prose prose-slate prose-lg max-w-none">
          <p className="text-xl text-gray-600 leading-relaxed mb-8 not-prose">
            {translatedSummary[activeLanguage as Exclude<Lang, 'English'>].intro}
          </p>
          {translatedSummary[activeLanguage as Exclude<Lang, 'English'>].sections.map((section) => (
            <React.Fragment key={section.heading}>
              <h2 className="text-2xl font-black text-[#0d2744] mt-12 mb-4">{section.heading}</h2>
              {section.paragraphs.map((paragraph, idx) => (
                <p key={`${section.heading}-${idx}`}>{paragraph}</p>
              ))}
            </React.Fragment>
          ))}
          <p>{translatedSummary[activeLanguage as Exclude<Lang, 'English'>].closing}</p>
          <div className="not-prose mt-12 p-6 bg-gray-50 border border-gray-200 rounded-2xl">
            <p className="text-sm text-gray-600 italic leading-relaxed">
              <strong className="text-[#0d2744]">{translatedSummary[activeLanguage as Exclude<Lang, 'English'>].series}</strong>
            </p>
          </div>
        </article>
      )}

      <article hidden={!isEnglishContent} className="max-w-3xl mx-auto px-6 lg:px-10 py-16 prose prose-slate prose-lg max-w-none">
        <p className="text-xl text-gray-600 leading-relaxed mb-8 not-prose">
          Lab results can feel like a report card, but for pets they’re simply signals. The goal is
          to read them with context—so you and your vet can make calm, confident decisions.
        </p>

        <p>
          A single number is a snapshot. Trends are the story. That’s why KodaCare helps you pair
          lab results with daily notes like appetite, energy, and breathing.
        </p>

        <h2 className="text-2xl font-black text-[#0d2744] mt-12 mb-4">
          1. What Labs Are Actually Measuring
        </h2>

        <p>
          Most wellness panels include a complete blood count, chemistry values, and sometimes a
          urinalysis. Together they look at inflammation, organ function, hydration, and metabolic
          balance. No single value stands alone.
        </p>

        <h2 className="text-2xl font-black text-[#0d2744] mt-12 mb-4">
          2. Trends Beat Single Numbers
        </h2>

        <p>
          Hydration, stress, time of day, and recent activity can shift results. A steady upward
          or downward pattern is far more important than a one‑off spike.
        </p>

        <p>
          KodaCare keeps a timeline of labs alongside vitals and behaviors, so you can quickly see
          whether a change is an outlier or part of a pattern.
        </p>

        <h2 className="text-2xl font-black text-[#0d2744] mt-12 mb-4">
          3. Pair Labs with Daily Context
        </h2>

        <p>
          If appetite dipped, breathing rate increased, or medication changed, those details
          matter just as much as the lab values. Shared context helps your vet interpret what’s
          happening and choose the next step.
        </p>

        <h2 className="text-2xl font-black text-[#0d2744] mt-12 mb-4">
          4. Ask Clearer Questions at the Visit
        </h2>

        <ul className="space-y-4 my-6 not-prose list-none pl-0">
          <li className="flex gap-3">
            <span className="mt-1 w-2 h-2 rounded-full bg-primary-brown shrink-0" />
            <span>Which values changed meaningfully over time?</span>
          </li>
          <li className="flex gap-3">
            <span className="mt-1 w-2 h-2 rounded-full bg-primary-brown shrink-0" />
            <span>Could recent meds, diet, or stress explain this shift?</span>
          </li>
          <li className="flex gap-3">
            <span className="mt-1 w-2 h-2 rounded-full bg-primary-brown shrink-0" />
            <span>What should we monitor daily before the next test?</span>
          </li>
        </ul>

        <h2 className="text-2xl font-black text-[#0d2744] mt-12 mb-4">
          From Results to Decisions
        </h2>

        <p>
          When labs live alongside daily observations, you move from worry to action. KodaCare
          turns results into a story that your whole care team can understand.
        </p>

        <div className="not-prose mt-12 p-6 bg-gray-50 border border-gray-200 rounded-2xl">
          <p className="text-sm text-gray-600 italic leading-relaxed">
            <strong className="text-[#0d2744]">KodaCare Journal · Article 3 of 4</strong>
            <br />
            This is the third article in the KodaCare Journal. Next: The Habit Loop and how to
            build daily routines that stick.
          </p>
        </div>
      </article>

      <div className="border-t border-gray-100 py-12 bg-gray-50">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <p className="text-sm text-gray-500">{t('blog.moreFromTeam')}</p>
            <p className="font-bold text-[#0d2744] text-lg">{t('blog.readAllArticles')}</p>
          </div>
          <button
            onClick={() => navigate(websitePath('/blog'))}
            className="px-8 py-3 bg-[#0d2744] text-white rounded-xl font-bold hover:bg-[#1a3a5c] transition-colors"
          >
            ← {t('blog.backToBlog')}
          </button>
        </div>
      </div>
    </main>
  );
}
